import { RestartAlt } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { admiDataCollectionService } from '../../real-estate-asset-management/admi-data-collection.service';
import {
    buildFieldTree,
    type FieldValueMap,
    isFieldRequired,
    isFieldVisible,
} from '../../real-estate-asset-management/components/data-collection.helpers';
import { ListFieldRenderer, type ListEntry } from '../../real-estate-asset-management/components/ListFieldRenderer';
import { RequirementFieldInput } from '../../real-estate-asset-management/components/RequirementFieldInput';
import { SelectFieldInput } from '../../real-estate-asset-management/components/SelectFieldInput';
import type {
    ProductRequirementGroup,
    RequirementFieldOption,
    RequirementFieldWithChildren,
} from '../../real-estate-asset-management/data-collection.dto';
import { isProblemDetailTextOr } from 'src/common/ProblemDetail';

import type { ProductSummary } from '../product-requirements-config.dto';
import { type SimulatedStatus, simulateAvailability } from '../preview/availability-simulator';

// The preview has no REA behind it — building/service dropdowns get demo
// options so LIST rows and select fields stay usable in the sandbox.
const STUB_DYNAMIC_OPTIONS: Record<string, RequirementFieldOption[]> = {
    buildings: [
        { value: 'preview-building-1', label: '(1/2) — Musterstraße 1' },
        { value: 'preview-building-2', label: '(2/2) — Musterstraße 2' },
    ],
    services: [{ value: 'preview-service-1', label: 'PV 2 (Demo-Deal)' }],
};

const STATUS_PRESENTATION: Record<SimulatedStatus, { color: 'error' | 'warning' | 'success' }> = {
    MANDATORY_INFO_MISSING: { color: 'error' },
    IMPORTANT_INFO_MISSING: { color: 'warning' },
    ALL_AVAILABLE: { color: 'success' },
};

const STATUS_LABEL: Record<SimulatedStatus, string> = {
    MANDATORY_INFO_MISSING: 'Blocked — mandatory info missing',
    IMPORTANT_INFO_MISSING: 'Ready — priority 2',
    ALL_AVAILABLE: 'Ready — priority 1',
};

interface Props {
    products: ProductSummary[];
}

/**
 * Sandbox rendering of the customer data-collection form for one product,
 * reusing the READI renderers and condition evaluator — what a PM sees here is
 * what the customer gets. Answers live in local state only (never saved); a
 * side panel simulates the resulting information-availability status.
 */
export default function PreviewPane({ products }: Props) {
    const { t } = useTranslation();

    const [productId, setProductId] = useState<string>('');
    const [groups, setGroups] = useState<ProductRequirementGroup[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [values, setValues] = useState<FieldValueMap>({});

    // Loading/reset state is set in the select's onChange (not synchronously in
    // the effect — react-hooks/set-state-in-effect); the effect only runs the
    // fetch and commits its async results.
    useEffect(() => {
        if (!productId) return;
        let cancelled = false;
        admiDataCollectionService
            .getProductRequirementGroups(productId)
            .then((next) => {
                if (!cancelled) setGroups(next);
            })
            .catch((e: unknown) => {
                if (!cancelled) setError(isProblemDetailTextOr(e, t('Failed to load the requirement groups.')));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [productId, t]);

    const handleSelectProduct = (next: string) => {
        setProductId(next);
        setLoading(true);
        setError(null);
        setValues({});
        setGroups(null);
    };

    const simulation = useMemo(() => (groups ? simulateAvailability(groups, values) : null), [groups, values]);
    const missing = simulation?.verdicts.filter((v) => v.missing) ?? [];

    const setValue = (code: string, next: unknown) => setValues((v) => ({ ...v, [code]: next }));

    const renderField = (field: RequirementFieldWithChildren) => {
        const required = isFieldRequired(field, values);

        if (field.fieldType === 'list') {
            return (
                <ListFieldRenderer
                    key={field.id}
                    field={field}
                    entries={(values[field.code] as ListEntry[] | undefined) ?? []}
                    dynamicOptions={STUB_DYNAMIC_OPTIONS}
                    parentValues={values}
                    onChange={(entries) => setValue(field.code, entries)}
                />
            );
        }

        if (field.fieldType === 'select') {
            const options =
                field.optionSource === 'static'
                    ? (field.options ?? [])
                    : (STUB_DYNAMIC_OPTIONS[field.optionSource ?? ''] ?? []);
            const raw = values[field.code];
            const value = field.multiple
                ? Array.isArray(raw)
                    ? (raw as string[])
                    : []
                : typeof raw === 'string'
                  ? raw
                  : null;
            return (
                <SelectFieldInput
                    key={field.id}
                    field={field}
                    value={value}
                    options={options}
                    required={required}
                    onChange={(next) => setValue(field.code, next)}
                />
            );
        }

        if (field.fieldType === 'document') {
            // No upload in the sandbox — a checkbox stand-in keeps document
            // fields participating honestly in the status simulation.
            return (
                <Box key={field.id}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values[field.code] != null}
                                onChange={(e) => setValue(field.code, e.target.checked ? 'simulated-upload' : null)}
                            />
                        }
                        label={t('{{label}} — simulate as provided', { label: field.label })}
                    />
                </Box>
            );
        }

        return (
            <RequirementFieldInput
                key={field.id}
                field={field}
                value={(values[field.code] as string | number | boolean | null | undefined) ?? null}
                required={required}
                onChange={(next) => setValue(field.code, next)}
            />
        );
    };

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 320 }}>
                    <InputLabel>{t('Preview form for product')}</InputLabel>
                    <Select
                        label={t('Preview form for product')}
                        value={productId}
                        onChange={(e) => handleSelectProduct(e.target.value)}
                    >
                        {products.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.sku} — {p.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button size="small" startIcon={<RestartAlt />} disabled={!productId} onClick={() => setValues({})}>
                    {t('Reset answers')}
                </Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                {t('Sandbox — answers here are never saved. Fill the form and watch the rules and status react.')}
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!productId && (
                <Typography variant="body2" color="text.secondary">
                    {t('Pick a product to preview its customer form.')}
                </Typography>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {!loading && productId && groups && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
                    <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, minWidth: 0, width: '100%' }}>
                        {groups.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {t('This product has no requirement fields assigned yet.')}
                            </Typography>
                        )}
                        <Stack spacing={3}>
                            {groups.map((group) => {
                                const visibleRoots = buildFieldTree(group.requirementFields).filter((f) =>
                                    isFieldVisible(f, values),
                                );
                                if (visibleRoots.length === 0) return null;
                                return (
                                    <Box key={group.id}>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                            {group.name}
                                        </Typography>
                                        <Stack spacing={2}>{visibleRoots.map(renderField)}</Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Paper>

                    {simulation && (
                        <Paper variant="outlined" sx={{ p: 2, width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                            <Typography variant="overline" color="text.secondary">
                                {t('Simulated service status')}
                            </Typography>
                            <Box sx={{ my: 1 }}>
                                <Chip
                                    color={STATUS_PRESENTATION[simulation.status].color}
                                    label={t(STATUS_LABEL[simulation.status])}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {simulation.status}
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="overline" color="text.secondary">
                                {t('Missing information')}
                            </Typography>
                            {missing.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {t('Nothing missing — all requirements satisfied.')}
                                </Typography>
                            ) : (
                                <List dense disablePadding>
                                    {missing.map((v) => (
                                        <ListItem
                                            key={v.code}
                                            disableGutters
                                            secondaryAction={
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color={v.criticality === 'mandatory' ? 'error' : 'warning'}
                                                    label={t(v.criticality)}
                                                />
                                            }
                                        >
                                            <ListItemText primary={v.label} secondary={v.code} />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    )}
                </Stack>
            )}
        </Box>
    );
}
